import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { BookOpen, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReaderLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('reader_token');
    if (token) {
      // Verify token is still valid
      axios.get(`${API}/reader/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => {
        // Token is valid, redirect to biblioteca
        navigate('/reader/biblioteca');
      })
      .catch(() => {
        // Token is invalid, clear it
        localStorage.removeItem('reader_token');
        localStorage.removeItem('reader_user');
      });
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/reader/login`, formData);
      
      localStorage.setItem('reader_token', response.data.token);
      localStorage.setItem('reader_user', JSON.stringify(response.data.user));

      toast.success('¡Bienvenido de vuelta!');
      navigate('/reader/biblioteca');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700">
            <BookOpen size={32} />
            <span className="font-serif text-2xl font-bold">Raíz Lecturas</span>
          </Link>
        </div>

        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2 text-center">
              Iniciar Sesión
            </h2>
            <p className="text-gray-500 text-center mb-6">
              Ingresá con tu email y contraseña
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    className="pl-10 border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Tu contraseña"
                    required
                    className="pl-10 border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white px-6 py-4 rounded-full font-semibold text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Ingresando...
                  </>
                ) : (
                  'Ingresar'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600">
                ¿No tenés cuenta?{' '}
                <Link 
                  to="/reader/register" 
                  className="text-amber-600 font-semibold hover:underline"
                >
                  Registrate gratis
                </Link>
              </p>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              Tu contraseña fue enviada a tu email cuando te registraste.
              <br />
              Si no la encontrás, revisá tu carpeta de spam.
            </p>
          </CardContent>
        </Card>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReaderLogin;
